import { createSlice, PayloadAction, ValidateSliceCaseReducers } from '@reduxjs/toolkit';
import { parseArrayUrlParam } from '@smx-ui-common/url/parse-url-array-param';

import { drawingSetSheetFragment$data } from '../graphql/__generated__/drawingSetSheetFragment.graphql';
import { SheetDataType, WebViewerStateType } from '../types/types';

export type IsUpdateAfterUploadDrawingSetVersionType = { [key: string]: boolean };
export type NavigationCurrentSheetType = { sheet: drawingSetSheetFragment$data; webViewerState: WebViewerStateType };

export interface DrawingsStateProps {
    isUpdateAfterUploadDrawingSetVersion: IsUpdateAfterUploadDrawingSetVersionType;
    currentTags: string[];
    currentSearchText?: string;
    bufferTags: { [key: string]: number[] };
    searchResult: { [key: string]: number };
    formDrawingSheetWasChanged: { [key: string]: boolean };
    isAndFilterTag: boolean;
    navigationCurrentSheet?: NavigationCurrentSheetType;
    historyNavigation: WebViewerStateType[];
    goToSheet: SheetDataType | null;
}

const initialState: DrawingsStateProps = {
    isUpdateAfterUploadDrawingSetVersion: {},
    currentTags: parseArrayUrlParam('tags'),
    bufferTags: {},
    searchResult: {},
    isAndFilterTag: true,
    goToSheet: null,
    formDrawingSheetWasChanged: {},
    historyNavigation: [],
};

export type SetterDrawingsParams = Partial<DrawingsStateProps>;

const drawingsReducers: ValidateSliceCaseReducers<
    DrawingsStateProps,
    {
        setterDrawings: {
            reducer: (
                state: DrawingsStateProps,
                action: PayloadAction<SetterDrawingsParams>,
            ) => void | DrawingsStateProps;
            prepare: (payload: SetterDrawingsParams) => { payload: SetterDrawingsParams };
        };
        updateHistoryNavigation: (state: DrawingsStateProps, action: PayloadAction<WebViewerStateType>) => void;
        setNavigationCurrentSheetAndChangeHistory: (
            state: DrawingsStateProps,
            action: PayloadAction<drawingSetSheetFragment$data | undefined>,
        ) => void;
        setNavigationCurrentSheetByIndex: (
            state: DrawingsStateProps,
            action: PayloadAction<{ index: number; sheet: drawingSetSheetFragment$data }>,
        ) => void;
        setIsUpdateAfterUploadDrawingSetVersion: (
            state: DrawingsStateProps,
            action: PayloadAction<IsUpdateAfterUploadDrawingSetVersionType>,
        ) => void;
        setSearchResult: (state: DrawingsStateProps, action: PayloadAction<{ [key: string]: number }>) => void;
        setBuffer: (state: DrawingsStateProps, action: PayloadAction<{ [key: string]: number[] }>) => void;
        setFormDrawingSheetWasChanged: (
            state: DrawingsStateProps,
            action: PayloadAction<{ [key: string]: boolean }>,
        ) => void;
    }
> = {
    setterDrawings: {
        reducer: (state, action) => {
            // Эта логика остается неизменной
            return { ...state, ...action.payload };
        },
        prepare: (payload) => {
            return { payload };
        },
    },
    updateHistoryNavigation: (state, action) => {
        const activeIndex = state.historyNavigation.findIndex((item) => item.active);
        if (activeIndex > -1) {
            state.historyNavigation[activeIndex] = {
                ...state.historyNavigation[activeIndex],
                ...action.payload,
                active: true,
            };
        }
    },
    setNavigationCurrentSheetByIndex: (state, action) => {
        state.historyNavigation = state.historyNavigation.map((history, index) => ({
            ...history,
            active: index === action.payload.index,
        }));
        state.navigationCurrentSheet = {
            sheet: action.payload.sheet,
            webViewerState: state.historyNavigation[action.payload.index],
        };
        state.goToSheet = null;
    },
    setNavigationCurrentSheetAndChangeHistory: (state, action) => {
        if (
            action.payload &&
            state.historyNavigation?.[state.historyNavigation.length - 1]?.id !== action.payload?.id
        ) {
            state.historyNavigation = [
                ...state.historyNavigation
                    .slice(0, state.historyNavigation.findIndex((item) => item.active) + 1)
                    .map((item) => ({
                        ...item,
                        active: false,
                    })),
                {
                    id: action.payload.id,
                    active: true,
                },
            ];
        } else {
            state.historyNavigation = state.historyNavigation.map((history, index) => ({
                ...history,
                active: index === state.historyNavigation.length - 1,
            }));
        }

        state.navigationCurrentSheet = action.payload
            ? { sheet: action.payload!, webViewerState: state.historyNavigation[state.historyNavigation.length - 1] }
            : undefined;
        state.goToSheet = null;
    },
    setIsUpdateAfterUploadDrawingSetVersion: (state, action) => {
        state.isUpdateAfterUploadDrawingSetVersion = action.payload;
    },
    setSearchResult: (state, action) => {
        state.searchResult = { ...state.searchResult, ...action.payload };
    },
    setBuffer: (state, action) => {
        const newState = { ...state.bufferTags };

        Object.keys(action.payload).forEach((key) => {
            if (newState[key] && Array.isArray(newState[key])) {
                newState[key].push(...action.payload[key]);
            } else {
                newState[key] = action.payload[key];
            }
        });

        state.bufferTags = newState;
    },
    setFormDrawingSheetWasChanged: (state, action) => {
        state.formDrawingSheetWasChanged = { ...state.formDrawingSheetWasChanged, ...action.payload };
    },
};

export const drawingsSlice = createSlice({
    name: 'drawings',
    initialState,
    reducers: drawingsReducers,
    extraReducers: () => {},
});

export const {
    setterDrawings,
    setIsUpdateAfterUploadDrawingSetVersion,
    setSearchResult,
    setBuffer,
    setFormDrawingSheetWasChanged,
    setNavigationCurrentSheetAndChangeHistory,
    setNavigationCurrentSheetByIndex,
    updateHistoryNavigation,
} = drawingsSlice.actions;

export const drawingsSliceReducer = drawingsSlice.reducer;
